import React, { useCallback, useState } from 'react';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { useMutation, useQuery, useQueryCache } from 'react-query';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { QueryKey } from 'api/queryKey';
import { useEnsureContext } from 'common/hooks';
import { useLocation, useParams } from 'react-router';
import { Button, message, Skeleton, Tabs } from 'antd';
import { FormikOnSubmit } from 'lib/formik.utils';
import { BusinessPartnerMedicalOperatorEditForm } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/shared/BusinessPartnerMedicalOperatorEditForm';
import { MedicalOperatorFormModel } from 'models/medicalOperator';
import { RemoveAssignedPatientsButton } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/BusinessPartnerMedicalOperatorPage/RemoveAssignedPatientsButton';
import { Header } from 'common/components/Header';
import { BusinessPartnerMedicalOperatorAssignedPatients } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/BusinessPartnerMedicalOperatorPage/BusinessPartnerMedicalOperatorAssignedPatients';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { BusinessPartnerMedicalOperatorTransferPatientModal } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/shared/BusinessPartnerMedicalOperatorTransferPatientModal';
import { FormattedMessage } from 'react-intl';
import { MedicalOperatorsAssignPatientsModal } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/BusinessPartnerMedicalOperatorsPage/MedicalOperatorsAssignPatientsModal';
import styled from 'styled-components';
import { BusinessPartnerImportPatientsModal } from 'pages/BusinessPartnerManagement/BusinessPartnerPatientsManagement/shared/BusinessPartnerImportPatientsModal';
const { TabPane } = Tabs;

enum TabKey {
  Personal = 'Personal',
  AssignedPatients = 'AssignedPatients',
}

const TransferButton = styled(Button)`
  margin: 0 0.5rem;
`;
export const BusinessPartnerMedicalOperatorPage = () => {
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const { medicalOperatorId } = useParams();
  const queryCache = useQueryCache();
  const redirectedTab = useLocation().state;

  const [checkedPatientsIds, setCheckedPatientsIds] = useState<number[]>([]);
  const [checkedDenyAccess, setCheckedDenyAccess] = useState(false);

  const [
    isTransferPatientsModalVisible,
    setIsTransferPatientsModalVisible,
  ] = useState(false);

  const [
    isAssignPatientsModalVisible,
    setIsAssignPatientsModalVisible,
  ] = useState(false);

  const [
    isImportPatientsModalVisible,
    setIsImportPatientsModalVisible,
  ] = useState(false);

  const [
    isPatientsmanagementButtonsVisible,
    setPatientsmanagementButtonsVisible,
  ] = useState(redirectedTab ? true : false);

  const { isLoading, data, refetch } = useQuery(
    [QueryKey.BusinessPartnerMedicalOperator, bpContext.id, medicalOperatorId],
    () => {
      return BusinessPartnersAPI.fetchMedicalOperatorById(
        bpContext.id,
        medicalOperatorId
      );
    }
  );

  const intlMessage = useFormatMessage();

  const [mutate, mutationHelpers] = useMutation(
    BusinessPartnersAPI.modifyMedicalOperatorById
  );

  const [mutateuserPermissions] = useMutation(
    BusinessPartnersAPI.updateUserPermissions
  );

  const permissionItems = [
    {
      permission: 'ADMIN_PANEL_ACCESS',
      scope: 'PARTNER',
      enabled: checkedDenyAccess,
      read: checkedDenyAccess,
      create: checkedDenyAccess,
      update: checkedDenyAccess,
      delete: checkedDenyAccess,
    },
    {
      permission: 'DOCTOR_PORTAL_ACCESS',
      scope: 'PARTNER',
      enabled: checkedDenyAccess,
      read: checkedDenyAccess,
      create: checkedDenyAccess,
      update: checkedDenyAccess,
      delete: checkedDenyAccess,
    },
  ];

  const removeSpacesFromForm = (formData: MedicalOperatorFormModel) => {
    formData.phoneNumber = formData.phoneNumber

      .replace(/\s/g, '')
      .replace(/^0+/, '');
    formData.prefixCode = formData.prefixCode.replace(/\s/g, '');
    return formData;
  };

  const handleSubmit: FormikOnSubmit<MedicalOperatorFormModel> = useCallback(
    (formData) => {
      refetch();
      const formDataToSend =
        formData.phoneNumber !== null
          ? removeSpacesFromForm(formData)
          : formData;

      mutate(
        {
          businessPartnerId: formDataToSend.partner
            ? formDataToSend.partner
            : bpContext.id,
          dto: { ...formDataToSend, userId: medicalOperatorId },
        },
        {
          onSuccess: () => {
            mutateuserPermissions({
              userId: medicalOperatorId,
              items: permissionItems,
            });
            message.success(
              intlMessage(
                { id: 'entity.updated' },
                {
                  name: `${formData.firstName} ${formData.lastName}`,
                }
              )
            );
            queryCache
              .invalidateQueries(QueryKey.BusinessPartnerMedicalOperator)
              .then();
          },
        }
      ).then();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bpContext, mutate, queryCache, intlMessage]
  );

  const invalidatePatients = useCallback(
    () =>
      queryCache.invalidateQueries(
        QueryKey.BusinessPartnerMedicalOperatorPatientsList
      ),
    [queryCache]
  );

  const [importPatientsMutate] = useMutation(
    BusinessPartnersAPI.importPatientsFromFileToMedicalOperator
  );

  const partnerToBeHardcodedInModal = data?.partnerDetails;

  const importPatientsRequest = (file: File | Blob) => {
    importPatientsMutate(
      {
        businessPartnerId: bpContext.id,
        medicalOperatorId: medicalOperatorId,
        dto: { file },
      },
      {
        onSuccess: (res) => {
          queryCache
            .invalidateQueries(
              QueryKey.BusinessPartnerMedicalOperatorPatientsList
            )
            .then();
          message.success(
            `${res.length} ${
              res.length === 1
                ? intlMessage({ id: 'medicalOperators.importPatient.success' })
                : intlMessage({ id: 'medicalOperators.importPatients.success' })
            }`
          );
          setIsImportPatientsModalVisible(false);
        },
      }
    );
  };

  return (
    <>
      {isLoading && <Skeleton />}
      {!isLoading && data && (
        <>
          <Header
            title={data.firstName + ' ' + data.lastName}
            extra={
              isPatientsmanagementButtonsVisible && [
                <RemoveAssignedPatientsButton
                  key="remove"
                  patientsIds={checkedPatientsIds}
                  disabled={checkedPatientsIds.length === 0}
                  onDeleted={() => {
                    setCheckedPatientsIds([]);
                    invalidatePatients();
                  }}
                  medicalOperatorId={medicalOperatorId}
                  medicalOperator={data}
                />,
                <TransferButton
                  key="transfer"
                  type="primary"
                  disabled={checkedPatientsIds.length === 0}
                  onClick={() => {
                    setIsTransferPatientsModalVisible(true);
                  }}
                >
                  <FormattedMessage
                    id="medicalOperators.transferPatients"
                    tagName={'span'}
                  />
                </TransferButton>,

                <Button
                  key="import"
                  type="primary"
                  onClick={() => {
                    setIsImportPatientsModalVisible(true);
                  }}
                >
                  <FormattedMessage id="patients.import" tagName="span" />
                </Button>,

                <Button
                  key="assign"
                  type="primary"
                  onClick={() => {
                    setIsAssignPatientsModalVisible(true);
                  }}
                >
                  <FormattedMessage id="patients.assign" tagName="span" />
                </Button>,
              ]
            }
          />

          <Tabs
            defaultActiveKey={
              redirectedTab ? TabKey.AssignedPatients : TabKey.Personal
            }
            onChange={(activeKey) => {
              activeKey === 'Personal'
                ? setPatientsmanagementButtonsVisible(false)
                : setPatientsmanagementButtonsVisible(true);
            }}
          >
            <TabPane
              tab={intlMessage({ id: 'medicalOperators.personal' })}
              key={TabKey.Personal}
            >
              <BusinessPartnerMedicalOperatorEditForm
                onSubmit={handleSubmit}
                loading={mutationHelpers.isLoading}
                initialValues={data}
                medicalOperatorId={medicalOperatorId}
                checkedDenyAccess={checkedDenyAccess}
                setCheckedDenyAccess={setCheckedDenyAccess}
              />{' '}
            </TabPane>

            <TabPane
              tab={intlMessage({ id: 'medicalOperators.assignedPatients' })}
              key={TabKey.AssignedPatients}
            >
              <BusinessPartnerMedicalOperatorAssignedPatients
                medicalOperatorId={medicalOperatorId}
                checkedPatientsIds={checkedPatientsIds}
                setCheckedPatientsIds={setCheckedPatientsIds}
              />
              {/* {footer} */}
              {checkedPatientsIds && (
                <BusinessPartnerMedicalOperatorTransferPatientModal
                  visible={isTransferPatientsModalVisible}
                  medicalOperatorId={medicalOperatorId}
                  medicalOperator={data}
                  partnerHardcoded={partnerToBeHardcodedInModal}
                  patientsIds={checkedPatientsIds}
                  onTransfered={() => {
                    setCheckedPatientsIds([]);
                    invalidatePatients();
                  }}
                  hideModal={() => {
                    setIsTransferPatientsModalVisible(false);
                  }}
                />
              )}
              {checkedPatientsIds && (
                <MedicalOperatorsAssignPatientsModal
                  visible={isAssignPatientsModalVisible}
                  medicalOperator={data}
                  medicalOperatorId={medicalOperatorId}
                  hideModal={() => {
                    setIsAssignPatientsModalVisible(false);
                  }}
                />
              )}
              <BusinessPartnerImportPatientsModal
                visible={isImportPatientsModalVisible}
                importRequest={importPatientsRequest}
                loading={isLoading}
                hideModal={() => {
                  setIsImportPatientsModalVisible(false);
                }}
              />
            </TabPane>
          </Tabs>
        </>
      )}
    </>
  );
};
